import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Checkbox, Button, Skeleton, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { USER_INFO_QUERY_KEY, useUserInfo } from 'app/hooks/useUserInfo';
import { cleanMarkdownContent } from 'utils/misc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { acceptTermsAndConditions, getContract } from '../services';

import { ErrorMessage, getErrorMessage } from 'utils/axios';
import '../../../../styles/markdown.css';
import { useContract } from '../contexts/ContractContext';
import { NotificationProps } from 'utils/types';

export const useContractModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: userInfo } = useUserInfo();
  const { showContractModal, setShowContractModal } = useContract();
  const hasShownModal = useRef(false);

  const [isAccepted, setIsAccepted] = useState(false);

  const { data: contractData } = useQuery({
    queryKey: ['terms-and-conditions'],
    queryFn: getContract,
    enabled: !!userInfo && !userInfo.termsAccepted,
  });

  const { mutate: acceptContract, isPending: isAcceptingContract } = useMutation({
    mutationKey: ['auth', 'acceptTerms'],
    mutationFn: (email: string) => acceptTermsAndConditions(email, contractData?.data.id ?? 1),
    onSuccess: () => {
      notification.success({
        message: t('apiSuccessMessages.acceptTerms'),
        ...NotificationProps,
      });
      setShowContractModal(false);
      // invalidate user info query as the staleTime is set to Infinity
      queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  useEffect(() => {
    if (userInfo && !userInfo.termsAccepted && !hasShownModal.current) {
      setShowContractModal(true);
      hasShownModal.current = true;
    }
  }, [userInfo]);

  useEffect(() => {
    return () => {
      hasShownModal.current = false;
    };
  }, []);

  const handleCheckboxChange = useCallback((e: CheckboxChangeEvent) => {
    setIsAccepted(e.target.checked);
  }, []);

  const handleAcceptContract = useCallback(() => {
    const email = userInfo?.email ?? '';
    acceptContract(String(email));
  }, [userInfo, contractData, acceptContract]);

  const ContractSkeleton = (
    <div className="flex flex-col">
      <Skeleton.Input active size="large" />
      <div className="body-regular overflow-y-auto w-full mt-4">
        <Skeleton active paragraph={{ rows: 20 }} />
      </div>
    </div>
  );

  const ContractModal = (
    <Modal
      open={showContractModal}
      closable={false}
      maskClosable={false}
      width={1500}
      height={'80%'}
      centered
      className="contract-modal"
      footer={
        <div className="flex justify-between items-center">
          <Checkbox disabled={!contractData} onChange={handleCheckboxChange}>
            {t('auth.signup.contract.accept.text')}
          </Checkbox>
          <Button
            type="primary"
            className="bg-blue-800"
            disabled={!isAccepted}
            loading={isAcceptingContract}
            onClick={handleAcceptContract}
            data-testid="submit-button"
          >
            {t('auth.signup.contract.accept.continue')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        <h2 className="text-primary-600 h1-bold pb-10">{t('auth.signup.contract.heading')}</h2>
        <div className="body-regular overflow-y-auto max-h-[60vh] whitespace-pre-wrap w-full">
          {contractData ? (
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {cleanMarkdownContent(contractData?.data.content ?? '')}
              </ReactMarkdown>
            </div>
          ) : (
            ContractSkeleton
          )}
        </div>
      </div>
    </Modal>
  );

  return ContractModal;
};
